import { z } from 'zod'

export const WEDDING_WEBSITE_TEMPLATE_KEYS = ['classic'] as const
export const PUBLIC_GUEST_RSVP_STATUSES = ['confirmed', 'declined', 'maybe'] as const
export const PUBLIC_PLUS_ONE_RSVP_STATUSES = ['unknown', 'confirmed', 'declined', 'maybe'] as const

export const weddingWebsiteTemplateKeySchema = z.enum(WEDDING_WEBSITE_TEMPLATE_KEYS)
export const publicGuestRsvpStatusSchema = z.enum(PUBLIC_GUEST_RSVP_STATUSES)
export const publicPlusOneRsvpStatusSchema = z.enum(PUBLIC_PLUS_ONE_RSVP_STATUSES)

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

export const rsvpQuestionSettingsSchema = z.object({
  collectAccommodation: z.boolean().default(false),
  collectTransport: z.boolean().default(false),
  collectDietaryRequirements: z.boolean().default(false),
  collectSpecialRequirements: z.boolean().default(false),
})

export const updateWeddingWebsiteSchema = z.object({
  enabled: z.boolean(),
  templateKey: weddingWebsiteTemplateKeySchema.default('classic'),
  ...rsvpQuestionSettingsSchema.shape,
})

export const submitGuestRsvpSchema = z
  .object({
    rsvpStatus: publicGuestRsvpStatusSchema,
    needsAccommodation: z.boolean().optional(),
    needsTransport: z.boolean().optional(),
    transportNote: clearableOptionalText(1000),
    dietaryRequirements: clearableOptionalText(1000),
    specialRequirements: clearableOptionalText(1000),
    plusOneRsvpStatus: publicPlusOneRsvpStatusSchema.optional(),
    plusOneName: clearableOptionalText(160),
    plusOneNeedsAccommodation: z.boolean().optional(),
    plusOneNeedsTransport: z.boolean().optional(),
    plusOneTransportNote: clearableOptionalText(1000),
    plusOneDietaryRequirements: clearableOptionalText(1000),
    plusOneSpecialRequirements: clearableOptionalText(1000),
  })
  .superRefine((value, context) => {
    if (value.rsvpStatus !== 'declined' && value.needsTransport && !value.transportNote) {
      context.addIssue({
        code: 'custom',
        path: ['transportNote'],
        message: 'Enter where transport will be needed.',
      })
    }

    if (
      value.plusOneRsvpStatus === 'confirmed' &&
      value.plusOneNeedsTransport &&
      !value.plusOneTransportNote
    ) {
      context.addIssue({
        code: 'custom',
        path: ['plusOneTransportNote'],
        message: 'Enter where transport will be needed.',
      })
    }

    if (value.plusOneRsvpStatus !== 'confirmed') {
      return
    }

    const plusOneName =
      typeof value.plusOneName === 'string' ? value.plusOneName.trim() : value.plusOneName

    if (!plusOneName) {
      context.addIssue({
        code: 'custom',
        path: ['plusOneName'],
        message: 'Enter the name of the plus one.',
      })
    }
  })

export type WeddingWebsiteTemplateKey = (typeof WEDDING_WEBSITE_TEMPLATE_KEYS)[number]
export type PublicGuestRsvpStatus = (typeof PUBLIC_GUEST_RSVP_STATUSES)[number]
export type PublicPlusOneRsvpStatus = (typeof PUBLIC_PLUS_ONE_RSVP_STATUSES)[number]
export type RsvpQuestionSettings = z.infer<typeof rsvpQuestionSettingsSchema>
export type UpdateWeddingWebsiteInput = z.infer<typeof updateWeddingWebsiteSchema>
export type SubmitGuestRsvpInput = z.infer<typeof submitGuestRsvpSchema>
